import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useBulkDeleteRiskFundEntries,
  useDeleteRiskFundEntry,
  useRiskFundEntries,
  useRiskFundSummary,
} from '../../services/httpServices/queries';
import type {
  RiskFundEntry,
  RiskFundSourceType,
} from '../../services/httpServices/riskFundService';
import { usePermissions } from '../../hooks/usePermissions';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { DatePicker } from '../../components/ui/DatePicker';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { joinFullName, patientInitials } from '../../utils/patientName';
import {
  AlertCircle,
  AlertTriangle,
  BedDouble,
  Calendar,
  Clock,
  Eye,
  FileText,
  Hash,
  Loader2,
  Phone,
  Receipt,
  Search,
  SlidersHorizontal,
  Trash2,
  UserRound,
  Wallet,
  X,
} from 'lucide-react';

const limit = 20;

const SOURCE_OPTIONS: { value: RiskFundSourceType | ''; label: string }[] = [
  { value: '', label: 'All sources' },
  { value: 'DIAGNOSTIC_RECEIPT', label: 'OPD Receipt' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'ADMISSION', label: 'IPD Admission' },
];

const SOURCE_META: Record<
  RiskFundSourceType,
  { label: string; className: string; icon: typeof Receipt }
> = {
  DIAGNOSTIC_RECEIPT: {
    label: 'OPD Receipt',
    className: 'bg-primary/10 text-primary',
    icon: Receipt,
  },
  INVOICE: {
    label: 'Invoice',
    className: 'bg-warning/10 text-warning',
    icon: FileText,
  },
  ADMISSION: {
    label: 'IPD Admission',
    className: 'bg-danger/10 text-danger',
    icon: BedDouble,
  },
};

function money(value: number | string | null | undefined): string {
  const n = Number(value) || 0;
  return `৳${n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Column date format: DD/MM/YY with the time (12h, AM/PM) underneath. */
function formatMovedDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

function formatMovedTime(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const hours24 = date.getHours();
  const hours = String(hours24 % 12 === 0 ? 12 : hours24 % 12).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const meridiem = hours24 < 12 ? 'AM' : 'PM';
  return `${hours}:${minutes} ${meridiem}`;
}

function patientName(entry: RiskFundEntry): string {
  const name = joinFullName(entry.patient || {});
  return name || entry.patient?.registrationNumber || 'Unknown patient';
}

function patientInitialsLabel(entry: RiskFundEntry): string {
  const initials = patientInitials(entry.patient || {});
  if (initials) return initials;
  return entry.patient?.registrationNumber?.slice(0, 2).toUpperCase() || '?';
}

/** How the source document is labelled: number when it has one, short id otherwise. */
function sourceLabel(entry: RiskFundEntry): string {
  if (entry.sourceNumber) return entry.sourceNumber;
  if (entry.sourceType === 'ADMISSION') {
    return `Admission ${entry.sourceId.slice(0, 8).toUpperCase()}`;
  }
  return entry.sourceId.slice(0, 8).toUpperCase();
}

type DeleteConfirm =
  | { mode: 'single'; entry: RiskFundEntry }
  | { mode: 'bulk' }
  | null;

export function RiskFund() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('billing:risk-fund:delete');

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [sourceType, setSourceType] = useState<RiskFundSourceType | ''>(() => {
    const value = params.get('sourceType');
    return value === 'INVOICE' ||
      value === 'DIAGNOSTIC_RECEIPT' ||
      value === 'ADMISSION'
      ? value
      : '';
  });
  const [dateFrom, setDateFrom] = useState(() => params.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState(() => params.get('dateTo') || '');
  const [showFilters, setShowFilters] = useState(
    () => Boolean(params.get('sourceType') || params.get('dateFrom') || params.get('dateTo')),
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirm>(null);
  const [viewing, setViewing] = useState<RiskFundEntry | null>(null);

  // Close the details modal on Escape and lock background scrolling while open.
  useEffect(() => {
    if (!viewing) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setViewing(null);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [viewing]);

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      sourceType: sourceType || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [debouncedSearch, sourceType, dateFrom, dateTo],
  );

  const { data, isLoading, error, isFetching } = useRiskFundEntries(
    page,
    limit,
    filters,
  );
  const entries = useMemo<RiskFundEntry[]>(
    () => data?.data || [],
    [data],
  );
  const meta = data?.meta;

  const summaryQuery = useRiskFundSummary();
  const summary = summaryQuery.data?.data;

  const deleteEntry = useDeleteRiskFundEntry();
  const bulkDeleteEntries = useBulkDeleteRiskFundEntries();
  const deleting = deleteEntry.isPending || bulkDeleteEntries.isPending;

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (sourceType) newParams.set('sourceType', sourceType);
    if (dateFrom) newParams.set('dateFrom', dateFrom);
    if (dateTo) newParams.set('dateTo', dateTo);
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, {
      replace: true,
    });
  }, [
    page,
    debouncedSearch,
    sourceType,
    dateFrom,
    dateTo,
    navigate,
    location.pathname,
  ]);

  // A filter change starts from page 1, but the initial mount must keep the
  // page supplied in the URL (deep links / back navigation).
  const filtersInitialised = useRef(false);
  useEffect(() => {
    if (!filtersInitialised.current) {
      filtersInitialised.current = true;
      return;
    }
    setPage(1);
  }, [debouncedSearch, sourceType, dateFrom, dateTo]);

  // Drop selections that are no longer on the visible page.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(entries.map((entry) => entry.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [entries]);

  const allSelected =
    entries.length > 0 && entries.every((entry) => selectedIds.has(entry.id));
  const someSelected = selectedIds.size > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        entries.forEach((entry) => next.delete(entry.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        entries.forEach((entry) => next.add(entry.id));
        return next;
      });
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearFilters = () => {
    setSearch('');
    setSourceType('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const hasFilters = Boolean(search || sourceType || dateFrom || dateTo);

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;

    const ids =
      deleteConfirm.mode === 'bulk'
        ? Array.from(selectedIds)
        : [deleteConfirm.entry.id];

    try {
      const res: any =
        deleteConfirm.mode === 'bulk'
          ? await bulkDeleteEntries.mutateAsync(ids)
          : await deleteEntry.mutateAsync(ids[0]);

      const deletedCount = res?.data?.deleted?.length ?? 0;
      if (deletedCount === 0) {
        toast.error('The selected Risk Fund entries were not found or were already deleted');
      } else {
        toast.success(
          `Reversed ${deletedCount} Risk Fund entr${deletedCount === 1 ? 'y' : 'ies'} — the outstanding balance was restored`,
        );
      }

      setDeleteConfirm(null);
      setSelectedIds(new Set());
      setViewing(null);
      if (page > 1 && deletedCount >= entries.length) {
        setPage((p) => Math.max(1, p - 1));
      }
    } catch (err: any) {
      toast.error(
        err?.message || 'Failed to reverse the Risk Fund entry. Please try again.',
      );
    }
  };

  const cards = [
    {
      label: 'Total in Risk Fund',
      value: money(summary?.total),
      hint: `${summary?.count ?? 0} entr${summary?.count === 1 ? 'y' : 'ies'}`,
      icon: Wallet,
      color: 'bg-danger/10 text-danger',
    },
    {
      label: 'This month',
      value: money(summary?.thisMonth),
      hint: `${summary?.thisMonthCount ?? 0} this month`,
      icon: Calendar,
      color: 'bg-warning/10 text-warning',
    },
    {
      label: 'OPD (receipts)',
      value: money(summary?.opd),
      hint: 'Diagnostic receipts',
      icon: Receipt,
      color: 'bg-primary/10 text-primary',
    },
    {
      label: 'IPD (invoices & admissions)',
      value: money(summary?.ipd),
      hint: 'Admission bills',
      icon: BedDouble,
      color: 'bg-danger/10 text-danger',
    },
  ];

  const viewingSource = viewing
    ? SOURCE_META[viewing.sourceType] || SOURCE_META.INVOICE
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-danger/10 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-danger" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text tracking-tight">Risk Fund</h1>
            <p className="text-sm text-text-muted">
              Uncollectible balances written off active receivables
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setShowFilters((s) => !s);
            }}
            className={`px-3 py-2 border rounded-lg text-sm transition-colors flex items-center gap-2 ${
              showFilters || hasFilters
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-text-muted hover:text-text'
            }`}
          >
            <SlidersHorizontal size={15} />
            Filters
          </button>
          {isFetching && (
            <span className="text-xs text-text-muted flex items-center gap-1.5">
              <Loader2 size={13} className="animate-spin" />
              Updating…
            </span>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map((card) => (
          <div key={card.label} className="bg-surface rounded-xl border border-border p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs text-text-muted uppercase truncate">{card.label}</p>
                <p className="text-xl font-bold text-text mt-1">{card.value}</p>
                <p className="text-xs text-text-muted mt-0.5">{card.hint}</p>
              </div>
              <div className={`p-2.5 rounded-lg shrink-0 ${card.color}`}>
                <card.icon size={18} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border p-4 space-y-3">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search patient, mobile, invoice / receipt number…"
              className="w-full pl-9 pr-9 py-2.5 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-2.5 text-sm text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors flex items-center gap-1.5 justify-center"
            >
              <X size={14} />
              Clear filters
            </button>
          )}
        </div>

        {showFilters && (
          <div className="flex flex-col md:flex-row md:items-center gap-3 pt-1">
            <select
              value={sourceType}
              onChange={(e) =>
                setSourceType(e.target.value as RiskFundSourceType | '')
              }
              className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              title="Filter by source type"
            >
              {SOURCE_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <DatePicker
                value={dateFrom}
                max={dateTo || undefined}
                onChange={setDateFrom}
                className="w-40"
                title="Moved from date"
              />
              <span className="text-text-muted text-sm">to</span>
              <DatePicker
                value={dateTo}
                min={dateFrom || undefined}
                onChange={setDateTo}
                className="w-40"
                title="Moved to date"
              />
            </div>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {canDelete && someSelected && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> entr
            {selectedIds.size === 1 ? 'y' : 'ies'} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDeleteConfirm({ mode: 'bulk' })}
              disabled={bulkDeleteEntries.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteEntries.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Reverse & delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteEntries.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Entries */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span className="text-sm font-medium">Failed to load the Risk Fund</span>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  {canDelete && (
                    <th className="px-4 py-3 w-10 text-left">
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        disabled={entries.length === 0}
                        title="Select all on this page"
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Patient
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Moved
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Document
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Reason
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Recorded by
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {entries.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canDelete ? 9 : 8}
                      className="px-4 py-16 text-center text-text-muted"
                    >
                      <AlertTriangle size={28} className="mx-auto mb-3 text-text-muted/40" />
                      <p className="font-medium text-text">No Risk Fund entries</p>
                      <p className="text-sm mt-1">
                        {hasFilters
                          ? 'No write-offs match the current filters.'
                          : 'Move an uncollectible receipt, invoice or admission to the fund to see it here.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  entries.map((entry) => {
                    const metaInfo = SOURCE_META[entry.sourceType] || SOURCE_META.INVOICE;
                    const Icon = metaInfo.icon;
                    return (
                      <tr
                        key={entry.id}
                        className="hover:bg-background/50 transition-colors"
                      >
                        {canDelete && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(entry.id)}
                              onChange={() => toggleSelectOne(entry.id)}
                              className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setViewing(entry)}
                            className="group flex items-center gap-2.5 text-left"
                            title="View patient & entry details"
                          >
                            <span className="w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-white transition-colors">
                              {patientInitialsLabel(entry)}
                            </span>
                            <span className="min-w-0">
                              <span className="block text-sm font-medium text-text group-hover:text-primary group-hover:underline underline-offset-2 transition-colors truncate">
                                {patientName(entry)}
                              </span>
                              {entry.patient?.mobileNumber && (
                                <span className="block text-xs text-text-muted mt-0.5">
                                  {entry.patient.mobileNumber}
                                </span>
                              )}
                            </span>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-muted whitespace-nowrap">
                          <div className="tabular-nums">{formatMovedDate(entry.movedAt)}</div>
                          <div className="text-xs text-text-muted/80 tabular-nums">
                            {formatMovedTime(entry.movedAt)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col items-start gap-1">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${metaInfo.className}`}
                            >
                              <Icon size={12} />
                              {metaInfo.label}
                            </span>
                            <span
                              className="text-[11px] font-mono text-text-muted"
                              title="Patient UHID"
                            >
                              ID: {entry.patient?.registrationNumber || '—'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-text whitespace-nowrap">
                          {sourceLabel(entry)}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-danger text-right whitespace-nowrap">
                          {money(entry.amount)}
                        </td>
                        <td className="px-4 py-3 text-sm text-text-muted max-w-xs">
                          <span className="line-clamp-2" title={entry.reason || undefined}>
                            {entry.reason || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-muted whitespace-nowrap">
                          {entry.movedByName || '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => setViewing(entry)}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                              title="View entry"
                            >
                              <Eye size={16} />
                            </button>
                            {canDelete && (
                              <button
                                type="button"
                                onClick={() =>
                                  setDeleteConfirm({ mode: 'single', entry })
                                }
                                className="p-1.5 rounded-lg hover:bg-danger/10 text-text-muted hover:text-danger transition-colors"
                                title="Reverse write-off and delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="entries"
          />
        </div>
      )}

      {/* Reversal confirmation */}
      <ConfirmDialog
        open={deleteConfirm !== null}
        title={
          deleteConfirm?.mode === 'bulk'
            ? `Reverse ${selectedIds.size} Risk Fund entr${selectedIds.size === 1 ? 'y' : 'ies'}?`
            : 'Reverse this write-off?'
        }
        description={
          <>
            The outstanding balance will be put back on the source document
            {deleteConfirm?.mode === 'single' && (
              <>
                {' '}
                (<strong>{sourceLabel(deleteConfirm.entry)}</strong>)
              </>
            )}
            , restoring it to active receivables, and the Risk Fund record will be
            deleted.
          </>
        }
        confirmLabel="Reverse & delete"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />

      {/* Patient & entry details */}
      {viewing && viewingSource && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setViewing(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="risk-fund-entry-title"
            className="relative bg-surface rounded-2xl border border-border w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative px-6 pt-6 pb-5 border-b border-border">
              <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-br from-danger/20 via-danger/5 to-transparent pointer-events-none" />
              <button
                type="button"
                onClick={() => setViewing(null)}
                className="absolute right-4 top-4 z-10 p-1.5 rounded-lg text-text-muted hover:text-text hover:bg-background transition-colors"
                title="Close"
              >
                <X size={18} />
              </button>
              <div className="relative flex items-start gap-4 pr-10">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-danger to-danger/60 text-white flex items-center justify-center text-lg font-bold shrink-0 shadow-lg shadow-danger/25">
                  {patientInitialsLabel(viewing)}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-danger">
                    <AlertTriangle size={12} />
                    Risk fund write-off
                  </span>
                  <h3
                    id="risk-fund-entry-title"
                    className="text-xl font-bold text-text leading-tight mt-1 truncate"
                  >
                    {patientName(viewing)}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {viewing.patient?.registrationNumber && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-background border border-border text-xs font-medium text-text-muted">
                        <Hash size={11} />
                        {viewing.patient.registrationNumber}
                      </span>
                    )}
                    {viewing.patient?.mobileNumber && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-background border border-border text-xs font-medium text-text-muted">
                        <Phone size={11} />
                        {viewing.patient.mobileNumber}
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${viewingSource.className}`}
                    >
                      <viewingSource.icon size={12} />
                      {viewingSource.label}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-y-auto px-6 py-5 space-y-4">
              <div className="relative overflow-hidden rounded-xl border border-danger/20 bg-gradient-to-br from-danger/10 via-danger/5 to-transparent p-4">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted flex items-center gap-1.5">
                      <Wallet size={12} />
                      Written-off amount
                    </p>
                    <p className="text-3xl font-bold text-danger mt-1 tabular-nums">
                      {money(viewing.amount)}
                    </p>
                  </div>
                  <div className="sm:text-right">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted flex items-center gap-1.5 sm:justify-end">
                      <Clock size={12} />
                      Moved to fund
                    </p>
                    <p className="text-sm font-medium text-text mt-1">
                      {formatDateTime(viewing.movedAt)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-xl border border-border bg-background/40 p-3.5">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted flex items-center gap-1.5">
                    <Hash size={12} />
                    Document
                  </p>
                  <p className="text-sm font-mono font-medium text-text mt-1.5 break-all">
                    {sourceLabel(viewing)}
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-background/40 p-3.5">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted flex items-center gap-1.5">
                    <UserRound size={12} />
                    Recorded by
                  </p>
                  <p className="text-sm font-medium text-text mt-1.5">
                    {viewing.movedByName || '—'}
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-background/40 p-3.5 sm:col-span-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted flex items-center gap-1.5">
                    <FileText size={12} />
                    Reason
                  </p>
                  <p className="text-sm text-text mt-1.5 whitespace-pre-wrap leading-relaxed">
                    {viewing.reason || 'No reason recorded for this write-off.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-t border-border bg-background/40">
              <p className="text-xs text-text-muted flex items-center gap-1.5">
                <Clock size={12} />
                Written off by{' '}
                <span className="font-medium text-text">
                  {viewing.movedByName || 'Unknown user'}
                </span>
              </p>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setViewing(null)}
                  className="px-4 py-2 rounded-lg border border-border text-text hover:bg-background transition-colors text-sm font-medium"
                >
                  Close
                </button>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteConfirm({ mode: 'single', entry: viewing });
                      setViewing(null);
                    }}
                    className="px-4 py-2 rounded-lg bg-danger text-white hover:bg-danger/90 transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <Trash2 size={15} />
                    Reverse & delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
